import { _ } from "../../polyglot/polyglot.js";
import { makeMonitorRepository } from "../repositoryBuilders.js";
import { object } from "klassified";
import CompanyUser from "../../../models/CompanyUser.js";
import DocumentNotificationDefinition from "../../../models/accounting/document/DocumentNotificationDefinition.js";
import DocumentNotificationSetting from "../../../models/accounting/document/DocumentNotificationSetting.js";
import entriesFactory from "../../../models/accounting/document/entriesFactory.js";
import notifier from "../../widgets/notifier/notifier.js";
import selection from "../../../models/accounting/document/selection.js";
import session from "../../session.js";
import UploadService from "../../services/UploadService.js";
import userFactory from "../../../models/userFactory.js";

/**
 * `documentService` is used as an Invoker in order to handle
 * the commands. Its also interacts with the repository to
 * actually perform the command actions.
 *
 * 	@param {number} spec.companyId - Id of the company owning the
 * 	documents
 *  @param {Repository} [spec.documentRepository] - The repository to
 *  fetch files and folders. A repository will be created if none is
 *  provided.
 */
const documentService = object.subclass((that, my) => {
	my.initialize = function (spec) {
		my.super(spec);
		my.companyId = spec.companyId;

		const antiForgeryToken = session.getAntiForgeryToken();

		my.repository =
			spec.documentRepository ||
			makeMonitorRepository({
				route: "/Document2/#companyId/#action/?id",
				defaultFilters: { companyId: my.companyId, action: "Info" },
				model: entriesFactory,
				antiForgeryToken,
			});

		my.uploadService = new UploadService({ uploadPath: that.uploadPath() });

		my.documentNotificationRepository = makeMonitorRepository({
			route: "/Document2/#companyId/DocumentNotification/?id",
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
			},
			antiForgeryToken,
		});

		my.companyUserRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/User",
			model: CompanyUser,
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
			},
			antiForgeryToken,
		});

		my.companyAgencyUserRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/AgencyUser",
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
			},
			model: userFactory,
			antiForgeryToken,
		});

		my.documentNotificationSettingRepository = function (currentLanguage) {
			return makeMonitorRepository({
				route: "/Document2/#companyId/DocumentNotification/Settings/#languageCode",
				defaultFilters: {
					companyId: session.getCompany().getLegacyId(),
					languageCode: currentLanguage,
				},
				model: DocumentNotificationSetting,
				antiForgeryToken,
			});
		};
	};

	let root;
	let currentFolder;

	// Only perform the initialization once, eg. if `root` is `undefined`.
	that.loadEntries = function (whenLoaded) {
		if (root) {
			whenLoaded();
			return;
		}

		my.getRootEntry(whenLoaded, () => {
			notifier.error(_("Could not load document archive"));
		});
	};

	that.getCurrentFolder = function () {
		return currentFolder;
	};

	that.getRoot = function () {
		return root;
	};

	that.setCurrentFolder = function (folder) {
		currentFolder = folder;
	};

	that.getChildrenOf = function (id) {
		return root.findChildWithId(id);
	};

	/**
	 * Request a file. If `forceDownload` is truthy, try to force the web
	 * browser to download the file instead of opening it.
	 */
	that.downloadFile = function ({ file, forceDownload }) {
		// Use an anchor with a 'download' attribute to force the
		// browser to download the file instead of opening it.  Note
		// IE can still try to open the file.
		const anchor = document.createElement("a");

		if (forceDownload) {
			anchor.setAttribute("download", true);
		}

		anchor.setAttribute(
			"href",
			`/Document2/${my.companyId}/Download/${file.id()}`,
		);

		anchor.setAttribute("target", "_blank");
		anchor.setAttribute("style", "display: none;");

		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
	};

	that.uploadPath = function () {
		return `/Document2/${my.companyId}/Upload/`;
	};

	that.export = function () {
		window.open(`/Document2/${my.companyId}/Export/`, "_blank");
	};

	that.getNotifiableUsers = function (callback) {
		my.companyUserRepository.get().then((companyUsers) => {
			my.companyAgencyUserRepository.get().then((agencyUsers) => {
				callback(companyUsers, agencyUsers);
			});
		});
	};

	that.getDocumentNotificationSettings = function (callback) {
		my.documentNotificationSettingRepository(session.getLanguage())
			.get()
			.then((settings) => {
				that.getNotifiableUsers((companyUsers, agencyUsers) => {
					settings.setNotificationUsers({
						companyUsers,
						agencyUsers,
					});

					callback(settings);
				});
			});
	};

	that.invoke = function (aCommand) {
		aCommand.perform();
	};

	that.executeCommand = function () {
		// do nothing
	};

	that.executeCreateDirectoryCommand = async function (aCommand, callback) {
		const folderName = aCommand.directoryName();

		const model = {
			parentId: aCommand.getFolder().id(),
			typeOfNode: "folder",
			name: folderName,
		};

		const newRoot = await my.repository.save({
			model,
			successMessage: _('"{folderName}" folder is created successfully', {
				folderName,
			}),
			errorMessage: _('Could not create folder "{folderName}"', {
				folderName,
			}),
		});

		root = newRoot;
		callback();
	};

	that.executeDeleteCommand = function (aCommand, callback) {
		selection.forEach(
			(each, forEachCallback) => {
				my.removeEntry(each.entry(), forEachCallback, () => {
					notifier.error(
						_('Could not delete "{name}"', {
							name: each.entry().name(),
						}),
					);

					forEachCallback();
				});
			},
			() => {
				notifier.success(
					_(
						'"{item}" is deleted successfully',
						"{numberOfItems} items are deleted successfully",
						selection.numberOfItems(),
						{
							numberOfItems: selection.numberOfItems(),
							item:
								selection.numberOfItems() === 1
									? selection.firstEntry().name()
									: "",
						},
					),
				);

				callback();
			},
		);
	};

	that.executeDownloadCommand = function () {
		// Left empty on purpose.
		// the command execution will call `that.downloadFile()`
		// This hook can still be useful for logging, etc.
	};

	that.executeExportCommand = function () {
		// Left empty on purpose.
		// the command execution will call `that.export()`
		// This hook can still be useful for logging, etc.
	};

	that.executeEditPermissionsCommand = function (aCommand, callback) {
		my.updatePermissions(
			aCommand.getEntry(),
			() => {
				notifier.success(
					_(
						'Permissions for "{folderName}" folder are updated successfully',
						{
							folderName: aCommand.getEntry().name(),
						},
					),
				);

				callback();
			},
			() => {
				notifier.error(
					_('Could not update permissions for "{name}"', {
						name: aCommand.getEntry().name(),
					}),
				);
			},
		);
	};

	that.executeMoveCommand = function (aCommand, callback) {
		let id;
		const destination = aCommand.getDestination();

		if (destination === null) {
			id = null;
		}
		if (destination.isFile()) {
			id = destination.parent().id();
		} else {
			id = destination.id();
		}

		id ||= "";

		selection.forEach(
			(each, forEachCallback) => {
				my.updateParent({
					entry: each.entry(),
					parentId: id.toString(),
					onSuccess: forEachCallback,
					onError: () => {
						notifier.error(
							_('Could not move "{name}"', {
								name: each.entry().name(),
							}),
						);

						forEachCallback();
					},
				});
			},
			() => {
				notifier.success(
					_(
						'"{item}" is moved to "{destination}" folder successfully',
						'{numberOfItems} items are moved to "{destination}" folder successfully',
						selection.numberOfItems(),
						{
							numberOfItems: selection.numberOfItems(),
							item:
								selection.numberOfItems() === 1
									? selection.firstEntry().name()
									: "",
							destination: destination.name(),
						},
					),
				);

				callback();
			},
		);
	};

	that.executeRenameCommand = function (aCommand, callback) {
		const name = aCommand.newName();

		my.updateName({
			entry: aCommand.getEntry(),
			parent: aCommand.getFolder(),
			name,
			onSuccess: () => {
				notifier.success(_("Renaming is completed successfully"));
				callback();
			},
			onError: () => {
				notifier.error(_("Rename failed"));
			},
		});
	};

	/**
	 * Save the file of `aCommand`. The file was already uploaded by the form
	 * submission of the command widget.
	 */
	that.executeUploadCommand = function (aCommand, callback) {
		const name = aCommand.name();

		my.saveFile({
			parent: aCommand.getFolder(),
			name,
			notify: true,
			onSuccess: callback,
			onError: () => notifier.error(_("Upload failed")),
		});
	};

	/**
	 * Upload and save each file of `aCommand` sequentially.  No file was
	 * uploaded yet as this command is called from a drag & drop and not a file
	 * input in a form.
	 */
	that.executeMultipleUploadCommand = function (aCommand, callback) {
		const files = aCommand.getFiles();
		const folder = aCommand.getFolder();
		const fileUploadingCallback = aCommand.getFileUploadingCallback();

		my.uploadFilesSequentially({
			files,
			folder,
			fileUploadingCallback,
			callback,
		});
	};

	my.uploadFilesSequentially = function ({
		files,
		folder,
		fileUploadingCallback = () => {},
		callback = () => {},
	}) {
		const first = files[0];
		const rest = files.slice(1);

		fileUploadingCallback();

		function onError() {
			notifier.error(
				_('Upload of file "{filename}" failed', {
					filename: first.name,
				}),
			);
		}

		my.uploadService
			.uploadFile(first)
			.then(() =>
				my.saveFile({
					parent: folder,
					name: first.name,
					notify: rest.length === 0,
					onSuccess: () => {
						if (rest.length === 0) {
							callback();
						} else {
							my.uploadFilesSequentially({
								folder,
								fileUploadingCallback,
								callback,
								files: rest,
							});
						}
					},
					onError,
				}),
			)
			.catch(onError);
	};

	that.executeImportCommand = function (aCommand, callback) {
		const name = aCommand.getName();

		my.import(
			() => {
				notifier.success(
					_('Archive "{name}" is imported successfully', {
						name,
					}),
				);

				callback();
			},
			() => {
				notifier.error(_("Import archive failed"));
			},
		);
	};

	that.executeAgencyImportCommand = function (aCommand, callback) {
		const name = aCommand.getName();

		my.agencyImport(
			() => {
				notifier.success(
					_("Archive is imported successfully", { name }),
				);

				callback();
			},
			() => {
				notifier.error(_("Import archive failed"));
			},
		);
	};

	that.executeNotifyUsersCommand = function (aCommand, callback) {
		my.documentNotificationRepository
			.save({
				model: new DocumentNotificationDefinition({
					recipients: aCommand.getUsers().map((user) => user.getId()),
					// TODO improve this logic once we have timeline view
					// I currently rely on item selection being possible only in a folder
					folderId: my.getFolderIdForNotification(),
					setting: aCommand.getCustomSetting(),
				}),
				successMessage: _("Mail sent!"),
				errorMessage: _("Failed to send the notification!"),
			})
			.then(callback);
	};

	that.executeMarkAsPreliminary = function (aCommand, callback) {
		selection.forEach((w, fn) => {
			that.updateIsPreliminary({
				entry: w.entry(),
				isPreliminary: !w.entry().isPreliminary(),
				onSuccess: fn,
			});
		}, callback);
	};

	that.updateIsPreliminary = function ({
		entry,
		isPreliminary,
		onSuccess,
		onError,
	}) {
		const options = {
			filter: {
				id: entry.id(),
			},
			model: {
				parentId: entry.parent().id(),
				id: entry.id(),
				isPreliminary,
			},
			onError,
		};

		my.repository.update(options).then((newRoot) => {
			root = newRoot;
			onSuccess();
		});
	};

	that.resetCache = function () {
		root = undefined;
	};

	my.getRepository = function () {
		return my.repository;
	};

	my.getRootEntry = function (onSuccess, onError) {
		const options = {
			id: "",
			onError,
		};

		my.repository.get(options).then((newRoot) => {
			currentFolder = root = newRoot;
			onSuccess();
		});
	};

	my.removeEntry = function (entry, onSuccess, onError) {
		const options = {
			filter: {
				id: entry.id(),
			},
			onError,
			notify: false,
		};

		my.repository.remove(options).then((newRoot) => {
			root = newRoot;
			onSuccess();
		});
	};

	my.updateParent = function ({ entry, parentId, onSuccess, onError }) {
		const options = {
			filter: {
				id: entry.id(),
			},
			model: {
				isPreliminary: entry.isPreliminary(),
				id: entry.id(),
				parentId,
			},
			onError,
			notify: false,
		};

		my.repository.update(options).then((newRoot) => {
			root = newRoot;
			onSuccess();
		});
	};

	my.updatePermissions = function (entry, onSuccess, onError) {
		const options = {
			filter: {
				id: entry.id(),
			},
			model: entry,
			onError,
		};

		my.repository.update(options).then((newRoot) => {
			root = newRoot;
			onSuccess();
		});
	};

	my.saveFile = function ({ parent, name, notify, onSuccess, onError }) {
		const options = {
			model: {
				parentId: parent.id(),
				typeOfNode: "File",
				name,
			},
			notify,
			onError,
		};

		my.repository.save(options).then((newRoot) => {
			root = newRoot;
			onSuccess();
		});
	};

	my.import = function (onSuccess, onError) {
		const options = {
			filter: {
				action: "import",
			},
			onError,
		};

		my.repository.save(options).then((newRoot) => {
			root = newRoot;
			onSuccess();
		});
	};

	my.agencyImport = function (onSuccess, onError) {
		const options = {
			filter: {
				action: "import",
				fromAgency: true,
			},
			onError,
		};

		my.repository.save(options).then((newRoot) => {
			root = newRoot;
			onSuccess();
		});
	};

	my.updateName = function ({ entry, parent, name, onSuccess, onError }) {
		const options = {
			filter: {
				id: entry.id(),
			},
			model: {
				id: entry.id(),
				isPreliminary: entry.isPreliminary(),
				parentId: parent.id(),
				name,
			},
			onError,
		};

		my.repository.update(options).then((newRoot) => {
			root = newRoot;
			onSuccess();
		});
	};

	//
	// If we select a single folder, we assume we care about the contents of it,
	// Then we link to that folder.
	// Otherwise, we go to the parent folder of the first selected item.
	//
	// TODO: need to revise this logic once we have timeline view.
	//
	my.getFolderIdForNotification = function () {
		const folderToBeNotified = that.getFolderForNotification();
		return folderToBeNotified.id();
	};

	that.getFolderForNotification = function () {
		const items = selection.entries();
		const firstItem = items[0];

		if (items.length === 1 && firstItem.isFolder()) {
			return firstItem;
		}

		return firstItem.parent();
	};
});

documentService.class((that) => {
	// It is important to keep a single instance of
	// documentService per company around so that its cache is
	// shared. If each page has its own documentService, a page
	// won't see the changes done to a file through another
	// page. For example, in company settings, make a preliminary
	// file permanent. Then, go to the document archive: the file
	// is still marked as preliminary even though the file is now
	// permanent on the server.
	let instances = [];

	that.forCompanyId = function (companyId) {
		// eslint-disable-next-line sonarjs/no-empty-collection -- upstream issue: https://sonarsource.atlassian.net/browse/JS-258
		instances[companyId] ||= this({
			companyId,
		});

		// eslint-disable-next-line sonarjs/no-empty-collection -- upstream issue: https://sonarsource.atlassian.net/browse/JS-258
		return instances[companyId];
	};

	that.forCompany = function (company) {
		return that.forCompanyId(company.getLegacyId());
	};

	that.clearInstances = function () {
		instances = [];
	};
});

export default documentService;
